import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;

const client = axios.create({
  baseURL: `${BASE}/api`,
  withCredentials: true,  // send the HttpOnly auth cookie
});

// Auth is held in an HttpOnly cookie set by /api/auth/login. Logout calls
// /api/auth/logout which clears the cookie server-side.
export const clearToken = () => {
  // Fire-and-forget; we don't wait for the response on logout so the UI
  // stays snappy. Errors are intentionally swallowed.
  client.post("/auth/logout").catch(() => {});
};

client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      const url = (err.config?.url || "");
      // Only force-logout when verifying the token itself fails (/me).
      // Sign-in endpoints (/auth/login, /auth/google-session) and other 401s
      // (e.g. wrong-password on /me/password) surface to the caller.
      if (url.endsWith("/me")) {
        if (window.location.pathname !== "/") window.location.href = "/";
      }
    }
    return Promise.reject(err);
  }
);

export const api = {
  login: (username, password, remember_me = false) =>
    client.post("/auth/login", { username, password, remember_me }).then((r) => r.data),
  googleSessionExchange: (session_id) =>
    client.post("/auth/google-session", { session_id }).then((r) => r.data),
  me: () => client.get("/me").then((r) => r.data),
  changeMyPassword: (current_password, new_password) =>
    client.post("/me/password", { current_password, new_password }).then((r) => r.data),
  fiscalYears: () => client.get("/fiscal-years").then((r) => r.data),
  transactions: (cal_seq, offset = 0, limit = 24, filters = {}) =>
    client.get("/transactions", {
      params: {
        cal_seq, offset, limit,
        pye_id: filters.pye_id || undefined,
        codes_id: filters.codes_id || undefined,
        month: filters.month || undefined,
        pay_tp: (filters.pay_tp === 0 || filters.pay_tp) ? filters.pay_tp : undefined,
        txn_chk: filters.txn_chk || undefined,
      },
    }).then((r) => r.data),
  transactionsPending: (cal_seq, offset = 0, limit = 100) =>
    client.get("/transactions/pending", {
      params: { cal_seq, offset, limit },
    }).then((r) => r.data),
  monthlyTotals: (cal_seq, codes_id, pye_id) =>
    client.get("/monthly-totals", {
      params: { cal_seq, codes_id, pye_id: pye_id || undefined },
    }).then((r) => r.data),
  exportTransactionsUrl: (cal_seq) =>
    `${BASE}/api/transactions/export?cal_seq=${cal_seq}`,
  schema: () => client.get("/schema").then((r) => r.data),
  runSql: (sql, write_mode = false, row_limit = 1000) =>
    client.post("/sql/query", { sql, write_mode, row_limit }).then((r) => r.data),
  refs: () => client.get("/refs").then((r) => r.data),
  listCodes: () => client.get("/codes").then((r) => r.data),
  createCode: (payload) => client.post("/codes", payload).then((r) => r.data),
  updateCode: (codes_id, payload) =>
    client.put(`/codes/${encodeURIComponent(codes_id)}`, payload).then((r) => r.data),
  deleteCode: (codes_id) =>
    client.delete(`/codes/${encodeURIComponent(codes_id)}`).then((r) => r.data),

  listPayees: () => client.get("/payees").then((r) => r.data),
  readPayee: (pye_id) =>
    client.get(`/payees/${encodeURIComponent(pye_id)}`).then((r) => r.data),
  createPayee: (payload) => client.post("/payees", payload).then((r) => r.data),
  updatePayee: (pye_id, payload) =>
    client.put(`/payees/${encodeURIComponent(pye_id)}`, payload).then((r) => r.data),
  deletePayee: (pye_id) =>
    client.delete(`/payees/${encodeURIComponent(pye_id)}`).then((r) => r.data),

  listPayments: () => client.get("/payments").then((r) => r.data),
  createPayment: (payload) => client.post("/payments", payload).then((r) => r.data),
  updatePayment: (pay_tp, payload) =>
    client.put(`/payments/${pay_tp}`, payload).then((r) => r.data),
  deletePayment: (pay_tp) =>
    client.delete(`/payments/${pay_tp}`).then((r) => r.data),

  ledgers: () => client.get("/ledgers").then((r) => r.data),
  createLedger: (payload) => client.post("/ledgers", payload).then((r) => r.data),
  updateLedger: (lgr_id, patch) => client.patch(`/ledgers/${lgr_id}`, patch).then((r) => r.data),
  deleteLedger: (lgr_id) => client.delete(`/ledgers/${lgr_id}`).then((r) => r.data),

  budget: (lgr_id, cal_seq) =>
    client.get("/budget", { params: { lgr_id, cal_seq } }).then((r) => r.data),
  createTransaction: (payload) =>
    client.post("/transactions", payload).then((r) => r.data),
  updateTransaction: (txn_seq, payload) =>
    client.put(`/transactions/${txn_seq}`, payload).then((r) => r.data),
  deleteTransaction: (txn_seq) =>
    client.delete(`/transactions/${txn_seq}`).then((r) => r.data),
  listUsers: () => client.get("/users").then((r) => r.data),

  // ---- Clients (admin) ----
  listClients: () => client.get("/clients").then((r) => r.data),
  createClient: (payload) => client.post("/clients", payload).then((r) => r.data),
  updateClient: (id, patch) => client.patch(`/clients/${id}`, patch).then((r) => r.data),
  deleteClient: (id) => client.delete(`/clients/${id}`).then((r) => r.data),
  uploadClientLogo: (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post(`/clients/${id}/logo`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  deleteClientLogo: (id) => client.delete(`/clients/${id}/logo`).then((r) => r.data),

  // ---- Categories (admin) ----
  listCategories: (lgr_id) =>
    client.get("/categories", { params: { lgr_id: lgr_id || undefined } }).then((r) => r.data),
  createCategory: (payload) => client.post("/categories", payload).then((r) => r.data),
  updateCategory: (cat_seq, patch) => client.patch(`/categories/${cat_seq}`, patch).then((r) => r.data),
  deleteCategory: (cat_seq) => client.delete(`/categories/${cat_seq}`).then((r) => r.data),
  copyCategories: (payload) => client.post("/categories/copy", payload).then((r) => r.data),
  copyCategoriesFiltered: (payload) =>
    client.post("/categories/copy-filtered", payload).then((r) => r.data),
  createUser: (p) => client.post("/users", p).then((r) => r.data),
  updateUser: (login_id, p) => client.patch(`/users/${login_id}`, p).then((r) => r.data),
  deleteUser: (login_id) => client.delete(`/users/${login_id}`).then((r) => r.data),
  approveTransaction: (txn_seq, payload) =>
    client.post(`/transactions/${txn_seq}/approve`, payload).then((r) => r.data),

  // ---- Functions catalogue (admin write) ----
  listFunctions: () => client.get("/functions").then((r) => r.data),
  createFunction: (payload) => client.post("/functions", payload).then((r) => r.data),
  updateFunction: (function_id, payload) =>
    client.put(`/functions/${encodeURIComponent(function_id)}`, payload).then((r) => r.data),
  deleteFunction: (function_id) =>
    client.delete(`/functions/${encodeURIComponent(function_id)}`).then((r) => r.data),

  // ---- User authorisation (admin) ----
  getUserAuth: (user_id) =>
    client.get(`/userauth/${encodeURIComponent(user_id)}`).then((r) => r.data),
  setUserAuth: (user_id, functions) =>
    client.put(`/userauth/${encodeURIComponent(user_id)}`, { functions }).then((r) => r.data),

  // ---- Events calendar ----
  listEventcal: (view = "active") =>
    client.get("/eventcal", { params: { view } }).then((r) => r.data),
  createEventcal: (payload) =>
    client.post("/eventcal", payload).then((r) => r.data),
  updateEventcal: (seq, payload) =>
    client.put(`/eventcal/${seq}`, payload).then((r) => r.data),
  deleteEventcal: (seq, scope = "one") =>
    client.delete(`/eventcal/${seq}`, { params: { scope } }).then((r) => r.data),
  setEventcalRsvp: (seq, response) =>
    client.put(`/eventcal/${seq}/rsvp`, { response }).then((r) => r.data),
  getEventcalRespondents: (seq) =>
    client.get(`/eventcal/${seq}/respondents`).then((r) => r.data),
  eventcalIcsUrl: (seq) => `${BASE}/api/eventcal/${seq}/ics`,

  // Public (unauthenticated) landing-page agenda — no cookie needed.
  listPublicEventcal: () =>
    axios.get(`${BASE}/api/public/eventcal`, { timeout: 8000 }).then((r) => r.data),
  publicEventcalIcsUrl: (seq) => `${BASE}/api/public/eventcal/${seq}/ics`,
};
